import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, RotateCcw } from 'lucide-react';
import { useStory } from '../contexts/StoryContext';
import { DialogueBox } from '../components/Story/DialogueBox';
import { ChoiceButton } from '../components/Story/ChoiceButton';
import { ProgressBar } from '../components/Story/ProgressBar';
import { InvestigationScene } from '../components/Story/InvestigationScene';
import { Notebook } from '../components/Story/Notebook';
import { UnlockNotification } from '../components/Story/UnlockNotification';
import { StoryUnlockService } from '../services/StoryUnlockService';

const StoryPage: React.FC = () => {
  const { state, engine, actions } = useStory();
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockedStory, setUnlockedStory] = useState<any>(null);
  const unlockService = StoryUnlockService.getInstance();

  useEffect(() => {
    // 页面加载时自动开始第一章
    if (!state.currentChapter) {
      actions.startChapter('chapter1');
    }
  }, [state.currentChapter, actions]);

  useEffect(() => {
    // 监听章节完成，检查解锁
    if (state.currentNode?.type === 'ending') {
      const rewards = unlockService.checkUnlocks(state.progress);
      if (rewards.length > 0) {
        const stories = unlockService.getUnlockedStories();
        if (stories.length > 0) {
          setUnlockedStory(stories[stories.length - 1]);
          setShowUnlock(true);

          // 应用奖励
          if (rewards[0].trustBonus) {
            // 更新信任度
            const progress = engine.getProgress();
            progress.trust['oldHan'] = (progress.trust['oldHan'] || 0) + rewards[0].trustBonus;
            engine.saveProgress();
          }
        }
      }
    }
  }, [state.currentNode]);

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  const handleViewStory = () => {
    setShowUnlock(false);
    // 跳转到海龟汤游戏模式
    window.location.href = '/game';
  };

  const handleCloseUnlock = () => {
    setShowUnlock(false);
  };

  const handleQuickSave = () => {
    engine.saveProgress();
    // 显示保存成功提示
    const toast = document.createElement('div');
    toast.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    toast.textContent = '进度已保存';
    document.body.appendChild(toast);
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 2000);
  };

  const handleReset = () => {
    if (window.confirm('确定要重置所有剧情进度吗？')) {
      actions.resetProgress();
      actions.startChapter('chapter1');
    }
  };

  if (!state.currentChapter || !state.currentNode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载剧情中...</p>
        </div>
      </div>
    );
  }

  const currentDialogue = state.currentNode.dialogues?.[state.currentDialogueIndex];
  const isLastDialogue = state.currentNode.dialogues?.length ?
    state.currentDialogueIndex === state.currentNode.dialogues.length - 1 :
    false;

  // 确保对话内容有效
  const safeCurrentDialogue = currentDialogue ? {
    ...currentDialogue,
    speaker: currentDialogue.speaker || '未知',
    content: currentDialogue.content || '',
    type: currentDialogue.type || 'dialogue'
  } : null;

  // 调试日志
  console.log('StoryPage Debug:', {
    currentNodeId: state.currentNode?.id,
    nodeType: state.currentNode?.type,
    dialogueIndex: state.currentDialogueIndex,
    hasDialogues: !!(state.currentNode?.dialogues?.length),
    dialoguesLength: state.currentNode?.dialogues?.length,
    currentDialogue: safeCurrentDialogue?.id,
    currentSpeaker: safeCurrentDialogue?.speaker,
    isLastDialogue,
    showChoices: state.showChoices,
    showInvestigation: state.showInvestigation
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* 顶部导航栏 */}
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="flex justify-between items-center max-w-4xl mx-auto">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBackToHome}
              className="flex items-center text-gray-600 hover:text-gray-900 transition"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              返回主页
            </button>
            <h1 className="text-xl font-bold">法底捞针 - 主线剧情</h1>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleQuickSave}
              className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              <Save className="w-4 h-4 mr-1" />
              保存
            </button>
            <button
              onClick={handleReset}
              className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              重置
            </button>
          </div>
        </div>
      </header>

      {/* 主要内容区 */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* 进度条 */}
        <ProgressBar
          progress={actions.getChapterProgress()}
          chapterTitle={state.currentChapter?.title ?? '未命名章节'}
        />

        {/* 调查场景 */}
        {state.currentNode?.type === 'investigation' && state.showInvestigation && (
          <InvestigationScene
            evidence={state.currentNode?.evidence ?? []}
            onEvidenceDiscovered={actions.discoverEvidence}
            onComplete={() => {
              // 调查完成后直接进入下一个节点
              if (state.currentNode?.nextNodeId) {
                engine.goToNode(state.currentNode.nextNodeId);
              }
              actions.toggleInvestigation();
            }}
          />
        )}

        {/* 对话框 */}
        {safeCurrentDialogue && (state.currentNode?.type === 'dialogue' || state.currentNode?.type === 'choice') && (
          <DialogueBox
            dialogue={safeCurrentDialogue}
            isActive={true}
            onComplete={() => {
              actions.nextDialogue();
            }}
            typewriterEffect={false}
          />
        )}

        {/* 选择按钮 */}
        {state.showChoices && state.currentNode.choices && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-3"
          >
            {state.currentNode?.choices?.map((choice, index) => (
              <ChoiceButton
                key={choice.id}
                choice={choice}
                onSelect={actions.makeChoice}
                index={index}
              />
            ))}
          </motion.div>
        )}

        {/* 系统消息（无选择时） */}
        {state.currentNode?.type === 'dialogue' && isLastDialogue && !state.showChoices && (
          <div className="text-center py-8">
            <p className="text-gray-500">
              敬请期待后续内容...
            </p>
          </div>
        )}

        {/* 章节结束 */}
        {state.currentNode.type === 'ending' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-4">章节完成</h2>
            <p className="text-gray-600 mb-8">
              恭喜你完成了第一章的剧情
            </p>
            <button
              onClick={handleBackToHome}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              返回主页
            </button>
          </motion.div>
        )}
      </main>

      {/* 笔记本 */}
      <Notebook
        entries={engine.getNotes()}
        onAddEntry={actions.addNote}
      />

      {/* 解锁通知 */}
      <UnlockNotification
        show={showUnlock}
        storyTitle={unlockedStory?.title || ''}
        rewards={unlockedStory?.rewards}
        onClose={handleCloseUnlock}
        onViewStory={handleViewStory}
      />
    </div>
  );
};

export default StoryPage;